import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { FaShieldAlt, FaLock, FaEye, FaCookie, FaEnvelope, FaPhone } from 'react-icons/fa';
import './PrivacyPage.css';

const PrivacyPage = () => {
  const lastUpdated = 'March 1, 2024';

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="privacy-page"
    >
      <Helmet>
        <title>Privacy Policy | ZustandTech</title>
        <meta
          name="description"
          content="Read ZustandTech's (Zustand Tech Software Private Limited) privacy policy to learn how we collect, use, and protect your personal data."
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.zustandtech.com/privacy" />
      </Helmet>

      <section className="privacy-content">
        <div className="container">
          {/* Page Heading */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="page-heading"
          >
            <h1>Privacy <span className="gradient-text">Policy</span></h1>
            <p>How we protect and handle your data</p>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="last-updated"
            >
              Last Updated: {lastUpdated}
            </motion.div>
          </motion.div>

          <div className="privacy-grid">
            {/* Sidebar Navigation */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="privacy-sidebar"
            >
              <h3>Quick Navigation</h3>
              <ul>
                <li><a href="#introduction">Introduction</a></li>
                <li><a href="#information">Information We Collect</a></li>
                <li><a href="#usage">How We Use Information</a></li>
                <li><a href="#sharing">Information Sharing</a></li>
                <li><a href="#security">Data Security</a></li>
                <li><a href="#cookies">Cookies</a></li>
                <li><a href="#rights">Your Rights</a></li>
                <li><a href="#changes">Policy Changes</a></li>
                <li><a href="#contact">Contact Us</a></li>
              </ul>
            </motion.div>

            {/* Main Content */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="privacy-main"
            >
              <div className="privacy-section" id="introduction">
                <h2>Introduction</h2>
                <p>
                  At ZustandTech, we take your privacy seriously. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services. Please read this privacy policy carefully. If you do not agree with the terms of this privacy policy, please do not access the site.
                </p>
                <p>
                  We reserve the right to make changes to this Privacy Policy at any time and for any reason. We will alert you about any changes by updating the "Last Updated" date of this Privacy Policy. Any changes or modifications will be effective immediately upon posting the updated Privacy Policy on the Site, and you waive the right to receive specific notice of each such change or modification.
                </p>
              </div>

              <div className="privacy-section" id="information">
                <h2>Information We Collect</h2>
                <h3>Personal Data</h3>
                <p>
                  While using our services, we may ask you to provide us with certain personally identifiable information that can be used to contact or identify you. Personally identifiable information may include, but is not limited to:
                </p>
                <ul>
                  <li>Name and contact information (email address, phone number)</li>
                  <li>Company name and job title</li>
                  <li>Billing and payment information</li>
                  <li>Project requirements and specifications</li>
                  <li>Communication preferences</li>
                </ul>

                <h3>Usage Data</h3>
                <p>
                  We may also collect information on how our services are accessed and used. This Usage Data may include information such as your computer's Internet Protocol address (IP address), browser type, browser version, the pages of our service that you visit, the time and date of your visit, the time spent on those pages, unique device identifiers and other diagnostic data.
                </p>
              </div>

              <div className="privacy-section" id="usage">
                <h2>How We Use Your Information</h2>
                <p>We use the information we collect for various purposes, including:</p>
                <ul>
                  <li>To provide and maintain our services</li>
                  <li>To notify you about changes to our services</li>
                  <li>To allow you to participate in interactive features</li>
                  <li>To provide customer support</li>
                  <li>To gather analysis or valuable information to improve our services</li>
                  <li>To monitor the usage of our services</li>
                  <li>To detect, prevent and address technical issues</li>
                  <li>To fulfill any other purpose for which you provide it</li>
                </ul>
              </div>

              <div className="privacy-section" id="sharing">
                <h2>Information Sharing and Disclosure</h2>
                <p>
                  We do not sell, trade, or rent your personal information to third parties. We may share your information in the following circumstances:
                </p>
                <ul>
                  <li><strong>With Service Providers:</strong> We may share your information with third-party vendors, service providers, contractors, or agents who perform services for us or on our behalf.</li>
                  <li><strong>For Business Transfers:</strong> If we are involved in a merger, acquisition, or asset sale, your information may be transferred.</li>
                  <li><strong>With Your Consent:</strong> We may disclose your personal information for any other purpose with your consent.</li>
                  <li><strong>Legal Requirements:</strong> We may disclose your information if required to do so by law or in response to valid requests by public authorities.</li>
                </ul>
              </div>

              <div className="privacy-section" id="security">
                <h2>Data Security</h2>
                <div className="security-features">
                  <div className="security-feature">
                    <FaLock className="feature-icon" />
                    <div>
                      <h4>Encryption</h4>
                      <p>All data transmitted between your browser and our servers is encrypted using SSL/TLS technology.</p>
                    </div>
                  </div>
                  <div className="security-feature">
                    <FaShieldAlt className="feature-icon" />
                    <div>
                      <h4>Access Control</h4>
                      <p>Strict access controls ensure that only authorized personnel can access your information.</p>
                    </div>
                  </div>
                  <div className="security-feature">
                    <FaEye className="feature-icon" />
                    <div>
                      <h4>Regular Audits</h4>
                      <p>We regularly audit our systems and processes to ensure your data remains secure.</p>
                    </div>
                  </div>
                </div>
                <p>
                  We have implemented measures designed to secure your personal information from accidental loss and from unauthorized access, use, alteration, and disclosure. However, no method of transmission over the internet or method of electronic storage is 100% secure.
                </p>
              </div>

              <div className="privacy-section" id="cookies">
                <h2>Cookies and Tracking Technologies</h2>
                <div className="cookie-info">
                  <FaCookie className="cookie-icon" />
                  <p>
                    We use cookies and similar tracking technologies to track activity on our services and hold certain information. Cookies are files with small amount of data which may include an anonymous unique identifier. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent.
                  </p>
                </div>
                <h3>Types of Cookies We Use:</h3>
                <ul>
                  <li><strong>Essential Cookies:</strong> Required for the website to function properly.</li>
                  <li><strong>Analytical/Performance Cookies:</strong> Help us understand how visitors interact with our website.</li>
                  <li><strong>Functionality Cookies:</strong> Remember choices you make to improve your experience.</li>
                </ul>
              </div>

              <div className="privacy-section" id="rights">
                <h2>Your Privacy Rights</h2>
                <p>Depending on your location, you may have the following rights regarding your personal information:</p>
                <ul>
                  <li><strong>Right to Access:</strong> You can request copies of your personal information.</li>
                  <li><strong>Right to Rectification:</strong> You can request that we correct inaccurate information.</li>
                  <li><strong>Right to Erasure:</strong> You can request that we delete your personal information.</li>
                  <li><strong>Right to Restrict Processing:</strong> You can request that we restrict the processing of your information.</li>
                  <li><strong>Right to Data Portability:</strong> You can request that we transfer your information to another organization.</li>
                  <li><strong>Right to Object:</strong> You can object to our processing of your personal information.</li>
                </ul>
                <p>
                  To exercise any of these rights, please contact us using the information provided below.
                </p>
              </div>

              <div className="privacy-section" id="changes">
                <h2>Changes to This Privacy Policy</h2>
                <p>
                  We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date at the top of this page. You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are effective when they are posted on this page.
                </p>
              </div>

              <div className="privacy-section" id="contact">
                <h2>Contact Us</h2>
                <p>
                  If you have any questions about this Privacy Policy, please contact us:
                </p>
                <div className="contact-details">
                  <p><FaEnvelope /> Email: info@zustandtech.com</p>
                  <p><FaPhone /> Phone: +91 (919) 605-45906</p>
                  <p>Address: 64 Gomti Nagar, Lucknow (UP) - 201030</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default PrivacyPage;